import { useEffect, useRef, useState } from "react";
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import {
  getInvoiceDetail,
  getJournalEntry,
  type InvoiceDetail,
  type JournalEntry,
  postInvoice,
} from "@/lib/api";

/** Never recreate or automatically retry a financial write after an uncertain response. */
export function InvoicePosting({
  invoice,
  lockDate,
  accountLabels,
  onInvoice,
  onRefresh,
}: {
  invoice: InvoiceDetail;
  lockDate?: string;
  accountLabels: Map<string, string>;
  onInvoice: (invoice: InvoiceDetail) => void;
  onRefresh: () => void;
}) {
  const [pending, setPending] = useState<InvoiceDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsCheck, setNeedsCheck] = useState(false);
  const [message, setMessage] = useState("");
  const [journal, setJournal] = useState<JournalEntry | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const eligible =
    invoice.status === "draft" &&
    /^[a-f0-9]{64}$/.test(invoice.posting_review_sha256 ?? "");
  const locked =
    !!lockDate && invoice.due_at.slice(0, 10) <= lockDate.slice(0, 10);
  const post = async () => {
    if (!pending || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const result = await postInvoice(
        pending.id,
        pending.posting_review_sha256!,
      );
      if (!mounted.current) return;
      if (
        result.id !== pending.id ||
        result.status !== "sent" ||
        !result.journal_entry_id
      ) {
        throw new Error("The response did not confirm this invoice's posting.");
      }
      setMessage(`Invoice posted. Journal ${result.journal_entry_id}`);
      setJournal(null);
      try {
        const entry = await getJournalEntry(result.journal_entry_id);
        if (mounted.current) setJournal(entry);
      } catch {
        // The posting itself is confirmed; a receipt-detail read failure must
        // not be reported as a posting failure.
      }
      if (!mounted.current) return;
      onRefresh();
    } catch {
      if (!mounted.current) return;
      setNeedsCheck(true);
      setMessage(
        "Posting was not confirmed. The invoice may have changed, the period may be locked, or the response may have been interrupted. Check current status before deciding what to do next.",
      );
    } finally {
      if (mounted.current) {
        setPending(null);
        setBusy(false);
      }
      inFlight.current = false;
    }
  };
  const check = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const current = await getInvoiceDetail(invoice.id);
      if (!mounted.current) return;
      if (
        current.id !== invoice.id ||
        current.organization_id !== invoice.organization_id ||
        current.legal_entity_id !== invoice.legal_entity_id
      ) {
        throw new Error("scope mismatch");
      }
      onInvoice(current);
      setNeedsCheck(false);
      setMessage(
        `Current status: ${current.status}. Review the invoice and journal before taking further action.`,
      );
    } catch {
      if (!mounted.current) return;
      setMessage(
        "Current status could not be checked. Posting remains blocked; try the status check again.",
      );
    } finally {
      if (mounted.current) setBusy(false);
      inFlight.current = false;
    }
  };
  return (
    <div className="grid gap-3">
      {eligible ? (
        <Button
          type="button"
          disabled={busy || needsCheck || locked}
          onClick={() => {
            setMessage("");
            setPending(invoice);
          }}
        >
          Post invoice
        </Button>
      ) : null}
      {eligible && locked ? (
        <p role="status">This invoice's due date is in a locked period.</p>
      ) : null}
      {message ? <p role="status">{message}</p> : null}
      {needsCheck ? (
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={check}
        >
          Check current status
        </Button>
      ) : null}
      {journal ? (
        <div
          className="surface grid gap-3 rounded-2xl p-4"
          data-testid="invoice-posted-journal"
        >
          <p className="text-sm font-medium">
            {journal.entry_number} · {journal.entry_date.slice(0, 10)}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {journal.lines.map((line, index) => (
                <TableRow key={`${line.account_id}-${index}`}>
                  <TableCell className="text-xs">
                    {accountLabels.get(line.account_id) ?? (
                      <span
                        className="text-[var(--muted-foreground)]"
                        title={line.account_id}
                      >
                        Unknown account
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {line.debit_cents > 0 ? (
                      formatMoney({
                        cents: line.debit_cents,
                        currency: invoice.currency_code,
                      })
                    ) : (
                      <span className="text-[var(--muted-foreground)]">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {line.credit_cents > 0 ? (
                      formatMoney({
                        cents: line.credit_cents,
                        currency: invoice.currency_code,
                      })
                    ) : (
                      <span className="text-[var(--muted-foreground)]">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Link
            to="/journal/$entryId"
            params={{ entryId: journal.id }}
            className="text-sm underline"
          >
            View journal
          </Link>
        </div>
      ) : null}
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !busy) setPending(null);
        }}
        title="Post this draft invoice?"
        description="Posts this invoice to its books and marks it sent. Does not move money, record a payment, or lock the period."
      >
        {pending ? (
          <div className="grid gap-4">
            <p>
              {pending.invoice_number} · {pending.customer_name} · due{" "}
              {new Date(pending.due_at).toLocaleDateString()}
            </p>
            <p>
              Total:{" "}
              {formatMoney({
                cents: pending.total_minor,
                currency: pending.currency_code,
              })}{" "}
              · {pending.lines.length}{" "}
              {pending.lines.length === 1 ? "line" : "lines"}
            </p>
            <div className="actions-row">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setPending(null)}
              >
                Cancel
              </Button>
              <Button type="button" disabled={busy} onClick={post}>
                {busy ? "Posting…" : "Post invoice"}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
